import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  Link,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Skeleton,
  Drawer,
  Tooltip,
  Chip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  LinearProgress,
  Fade
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
  CreateNewFolder as CreateIcon,
  Stop as StopIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import axios from 'axios';

interface FileData {
  id: string;
  name: string;
  webViewLink: string;
  createdTime: string;
  size?: string;
}

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalFiles: number;
  limit: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface OperationStatus {
  isRunning: boolean;
  progress: number;
  message: string;
  startTime?: string;
  total?: number;
  completed?: number;
  currentItem?: string;
  created?: number;
  skipped?: number;
  errors?: number;
  finishedAt?: string;
  error?: string;
  cancelled?: boolean;
  year?: number;
}



const TabelRB: React.FC = () => {
  const [folderId, setFolderId] = useState<string>('');
  const [editingFolderId, setEditingFolderId] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [creatingDocuments, setCreatingDocuments] = useState(false);
  const [files, setFiles] = useState<FileData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [operationStatus, setOperationStatus] = useState<OperationStatus | null>(null);
  const [showStatusIndicator, setShowStatusIndicator] = useState(false);
  const [isIndicatorHovered, setIsIndicatorHovered] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Year selection: current year and next year
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  useEffect(() => {
    document.title = 'Табель РБ | Zerde';
    loadFolderId();

    // Add keyframes for border animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    if (folderId) {
      loadFiles();
    }
  }, [folderId, currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    checkOperationStatus();
  }, []);

  // Polling for active operations
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (operationStatus && operationStatus.isRunning === true) {
      interval = setInterval(() => {
        checkOperationStatus();
      }, 2000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [operationStatus?.isRunning]);

  // Auto-hide status indicator after completion
  useEffect(() => {
    if (operationStatus && !operationStatus.isRunning && (operationStatus.finishedAt || operationStatus.cancelled)) {
      setShowStatusIndicator(true);

      const hideTimer = setTimeout(() => {
        setShowStatusIndicator(false);
        setOperationStatus(null);
      }, 15000);

      return () => clearTimeout(hideTimer);
    } else if (operationStatus?.isRunning) {
      setShowStatusIndicator(true);
    }
  }, [operationStatus]);

  const loadFolderId = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://tbl.zerdegroup.kz/api/v1/timesheet/folderID');

      if (response.data.success) {
        setFolderId(response.data.folderId);
        setEditingFolderId(response.data.folderId);
      } else {
        setError('Не удалось загрузить Folder ID');
      }
    } catch (err) {
      console.error('Error loading folder ID:', err);
      setError('Ошибка при загрузке Folder ID');
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async () => {
    try {
      setLoadingFiles(true);
      setError(null);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(`https://tbl.zerdegroup.kz/api/v1/timesheet/files/${folderId}?${params}`);

      if (response.data.success) {
        setFiles(response.data.files);
        setPagination(response.data.pagination);
      } else {
        setError('Не удалось загрузить список файлов');
      }
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Ошибка при загрузке файлов');
    } finally {
      setLoadingFiles(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  };

  const handleSearchChange = (newSearch: string) => {
    setSearchTerm(newSearch);
    setCurrentPage(1);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditingFolderId(folderId);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingFolderId(folderId);
  };

  const handleSaveClick = () => {
    if (editingFolderId !== folderId) {
      setConfirmDialog(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleConfirmChange = async () => {
    try {
      setLoading(true);
      const response = await axios.put('https://tbl.zerdegroup.kz/api/v1/timesheet/folderID', {
        folderId: editingFolderId
      });

      if (response.data.success) {
        setFolderId(editingFolderId);
        setSuccess('Folder ID успешно обновлен');
        setIsEditing(false);
      } else {
        setError('Не удалось обновить Folder ID');
      }
    } catch (err) {
      console.error('Error updating folder ID:', err);
      setError('Ошибка при обновлении Folder ID');
    } finally {
      setLoading(false);
      setConfirmDialog(false);
    }
  };

  const checkOperationStatus = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/v1/timesheet/operation-status');

      if (response.data.success) {
        const status = response.data.status;

        if (status.isRunning || operationStatus?.isRunning || showStatusIndicator) {
          setOperationStatus(status);
        }

        if (!status.isRunning && status.finishedAt && operationStatus?.isRunning) {
          const finishedTime = new Date(status.finishedAt).getTime();
          const now = new Date().getTime();
          if (now - finishedTime < 10000) {
            loadFiles();
          }
        }
      }
    } catch (err) {
      console.error('Error checking operation status:', err);
    }
  };

  const handleCreateDocuments = async () => {
    try {
      setCreatingDocuments(true);
      setError(null);
      setSuccess(`Запускаем создание документов за ${selectedYear} год...`);
      setSettingsOpen(false); // Close settings drawer

      const response = await axios.post('https://tbl.zerdegroup.kz/api/v1/timesheet/create-documents', {
        folderId,
        year: selectedYear
      });

      if (response.data.success) {
        setSuccess(`Создание документов за ${selectedYear} год запущено`);
        setShowStatusIndicator(true);
        checkOperationStatus();
      } else {
        setError('Не удалось запустить создание документов');
      }
    } catch (err: any) {
      console.error('Error creating documents:', err);

      if (err.response?.status === 409) {
        setError(err.response.data.message || 'Операция уже выполняется');
        checkOperationStatus();
      } else {
        setError('Ошибка при создании документов');
      }
    } finally {
      setCreatingDocuments(false);
    }
  };

  const handleCancelOperation = async () => {
    try {
      setError(null);
      const response = await axios.post('https://tbl.zerdegroup.kz/api/v1/timesheet/cancel-operation');

      if (response.data.success) {
        setSuccess('Операция отменена');
        checkOperationStatus();
      } else {
        setError('Не удалось отменить операцию');
      }
    } catch (err: any) {
      console.error('Error cancelling operation:', err);

      if (err.response?.status === 400) {
        setError(err.response.data.message || 'Нет активной операции для отмены');
      } else {
        setError('Ошибка при отмене операции');
      }
    }
  };

  const TableSkeleton = ({ rows = 10 }: { rows?: number }) => (
    <>
      {Array.from({ length: rows }).map((_, index) => (
        <TableRow key={index}>
          <TableCell sx={{ py: 1 }}>
            <Skeleton variant="text" width="70%" height={20} />
          </TableCell>
          <TableCell sx={{ py: 1 }}>
            <Skeleton variant="text" width="60px" height={18} />
          </TableCell>
          <TableCell sx={{ py: 1 }}>
            <Skeleton variant="text" width="100px" height={18} />
          </TableCell>
          <TableCell sx={{ py: 1 }}>
            <Skeleton variant="circular" width={28} height={28} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  const formatFileSize = (size?: string) => {
    if (!size) return 'N/A';
    const bytes = parseInt(size);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Floating Progress Indicator Component
  const FloatingProgressIndicator = () => {
    const theme = useTheme();

    if (!operationStatus || !showStatusIndicator) return null;

    const progress = operationStatus.progress || 0;
    const isRunning = operationStatus.isRunning;
    const isSuccess = !isRunning && !operationStatus.cancelled && !operationStatus.error;
    const isCancelled = operationStatus.cancelled;
    const hasError = operationStatus.error;

    // Define colors based on state
    const bgGradient = isRunning
      ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
      : isSuccess
        ? `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`
        : hasError
          ? `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`
          : `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.main} 100%)`; // Cancelled

    return (
      <Paper
        elevation={8}
        onMouseEnter={() => setIsIndicatorHovered(true)}
        onMouseLeave={() => setIsIndicatorHovered(false)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300,
          width: 320,
          overflow: 'hidden',
          borderRadius: 3,
          background: bgGradient,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.1)',
          cursor: 'pointer'
        }}
      >
        {/* Header - Always Visible */}
        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0
            }}>
              {isRunning ? (
                <CircularProgress size={18} thickness={5} sx={{ color: '#fff' }} />
              ) : isSuccess ? (
                <SuccessIcon sx={{ fontSize: 20 }} />
              ) : hasError ? (
                <ErrorIcon sx={{ fontSize: 20 }} />
              ) : (
                <WarningIcon sx={{ fontSize: 20 }} />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.3, fontSize: 13 }}>
                {isRunning ? 'Выполнение операции' : (isSuccess ? 'Готово!' : (isCancelled ? 'Отменено' : 'Ошибка'))}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.9,
                  display: 'block',
                  lineHeight: 1.3,
                  fontSize: 11,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: isIndicatorHovered ? 'normal' : 'nowrap'
                }}
              >
                {operationStatus.message || 'Обработка данных...'}
              </Typography>
            </Box>
          </Box>

          {!isRunning && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setShowStatusIndicator(false);
                setOperationStatus(null);
              }}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                flexShrink: 0,
                '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Expandable Content - Shows on Hover */}
        {isIndicatorHovered && (
          <Box>
            {/* Progress Bar */}
            <Box sx={{ px: 2, pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>Прогресс</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>{Math.round(progress)}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#fff',
                    borderRadius: 3
                  }
                }}
              />
            </Box>

            {/* Stats Grid */}
            <Box sx={{
              px: 2, pb: 2, pt: 1,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1.5
            }}>
              <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 1, borderRadius: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', fontSize: 10 }}>Обработано</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {operationStatus.completed || 0} <span style={{ opacity: 0.6 }}>/ {operationStatus.total || '?'}</span>
                </Typography>
              </Box>

              <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 1, borderRadius: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', fontSize: 10 }}>Создано</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#4ade80' }}>
                  {operationStatus.created || 0}
                </Typography>
              </Box>

              <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 1, borderRadius: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', fontSize: 10 }}>Пропущено</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#fbbf24' }}>
                  {operationStatus.skipped || 0}
                </Typography>
              </Box>

              <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 1, borderRadius: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', fontSize: 10 }}>Ошибки</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#f87171' }}>
                  {operationStatus.errors || 0}
                </Typography>
              </Box>
            </Box>

            {/* Actions */}
            {isRunning && (
              <Box sx={{ px: 2, pb: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelOperation();
                  }}
                  startIcon={<StopIcon />}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.9)',
                    color: theme.palette.error.main,
                    fontWeight: 700,
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#fff',
                    }
                  }}
                >
                  Отменить операцию
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  if (loading && !folderId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Floating Progress Indicator */}
      <FloatingProgressIndicator />

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Files table - main content without header */}
      {folderId && (
        <Paper sx={{ p: 2 }}>
          {/* Table header with title and controls */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Файлы {pagination && !loadingFiles ? `(${pagination.totalFiles})` : ''}
              </Typography>
              <Tooltip title="Обновить">
                <IconButton onClick={loadFiles} size="small" disabled={loadingFiles}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {pagination && !loadingFiles && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  Стр. {pagination.currentPage} из {pagination.totalPages}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Tooltip title="Настройки">
                <IconButton onClick={() => setSettingsOpen(true)} size="small">
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  sx={{ height: 32, fontSize: 13 }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>

              <TextField
                placeholder="Поиск..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                size="small"
                sx={{ width: 180 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  sx: { height: 32, fontSize: 13 }
                }}
              />
            </Box>
          </Box>

          <TableContainer sx={{ maxHeight: 'calc(100vh - 240px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, py: 1 }}>Название</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1, width: 80 }}>Размер</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1, width: 140 }}>Дата</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1, width: 50 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingFiles ? (
                  <TableSkeleton rows={itemsPerPage} />
                ) : (
                  <>
                    {files.map((file: FileData) => (
                      <TableRow key={file.id} hover>
                        <TableCell sx={{ py: 0.75 }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                            {file.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(file.size)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(file.createdTime)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          <IconButton
                            component={Link}
                            href={file.webViewLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {files.length === 0 && !loadingFiles && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {searchTerm ? 'Файлы не найдены' : 'Файлы отсутствуют'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {!loadingFiles && pagination && pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={(_, page) => handlePageChange(page)}
                color="primary"
                showFirstButton
                showLastButton
                size="small"
              />
            </Box>
          )}
        </Paper>
      )}

      {/* Settings Drawer */}
      <Drawer
        anchor="right"
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      >
        <Box sx={{ width: 380, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Настройки</Typography>
            <IconButton onClick={() => setSettingsOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Create Documents Section */}
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Создание документов
          </Typography>

          <ToggleButtonGroup
            value={selectedYear}
            exclusive
            onChange={(_, value) => value && setSelectedYear(value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            <ToggleButton value={currentYear} sx={{ flex: 1, fontWeight: 600 }}>
              {currentYear}
            </ToggleButton>
            <ToggleButton value={currentYear + 1} sx={{ flex: 1, fontWeight: 600 }}>
              {currentYear + 1}
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            size="large"
            startIcon={creatingDocuments ? <CircularProgress size={18} color="inherit" /> : <CreateIcon />}
            onClick={handleCreateDocuments}
            disabled={creatingDocuments || !folderId || (operationStatus?.isRunning || false)}
            fullWidth
            sx={{ mb: 3 }}
          >
            {operationStatus?.isRunning ? 'Выполняется...' : creatingDocuments ? 'Запуск...' : 'Создать документы'}
          </Button>

          <Divider sx={{ mb: 3 }} />

          {/* Folder ID Section */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Google Drive Folder ID
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              value={isEditing ? editingFolderId : folderId}
              onChange={(e) => setEditingFolderId(e.target.value)}
              disabled={!isEditing}
              placeholder="Введите Folder ID"
            />

            {!isEditing ? (
              <IconButton onClick={handleEditClick} color="primary" size="small">
                <EditIcon />
              </IconButton>
            ) : (
              <>
                <IconButton onClick={handleSaveClick} color="success" size="small">
                  <SaveIcon />
                </IconButton>
                <IconButton onClick={handleCancelEdit} color="error" size="small">
                  <CancelIcon />
                </IconButton>
              </>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary">
            ID папки в Google Drive, где хранятся все табели. Изменение повлияет на всех пользователей.
          </Typography>
        </Box>
      </Drawer>

      {/* Confirm folder ID change dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Подтвердите изменение</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите изменить Folder ID с <strong>{folderId}</strong> на <strong>{editingFolderId}</strong>?
          </Typography>
          <Typography color="warning.main" sx={{ mt: 1 }}>
            Это действие повлияет на всех пользователей системы.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Отмена</Button>
          <Button onClick={handleConfirmChange} variant="contained" color="warning">
            Подтвердить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TabelRB;
